/** Matches the viewport width the rest of the app treats as mobile. */
const MOBILE_QUERY = '(max-width: 576px)';

const GUTTER_WIDTH = '12px';
const NO_GUTTER = '0px';

/**
 * Detects whether the platform uses overlay scrollbars.
 *
 * Overlay scrollbars (macOS with "Show scroll bars" set to automatic, and most
 * touch platforms) paint on top of the content and report zero width, so
 * `scrollbar-gutter: stable` reserves nothing and the thumb covers whatever sits
 * at the right edge of a scroll container. Classic scrollbars (Windows, Linux,
 * macOS with "Always") already take up layout width, so no extra room is needed.
 * @returns Whether the platform draws overlay scrollbars.
 */
const detectOverlayScrollbars = () => {
  const probe = document.createElement('div');

  probe.style.cssText =
    'position:absolute;top:-9999px;width:100px;height:100px;overflow:scroll;';
  document.body.appendChild(probe);

  const isOverlay = probe.offsetWidth - probe.clientWidth === 0;

  probe.remove();

  return isOverlay;
};

/**
 * Exposes the gutter width as the `--scrollbar-overlay-gutter` CSS variable on
 * the root element, and keeps it in step with the viewport width.
 *
 * At mobile widths the gutter is dropped. A touch scrollbar only surfaces while
 * the user is scrolling and fades out again, so reserving room for it would
 * spend horizontal space on something that is almost never painted — and space
 * is scarcest on exactly those screens. The variable is written inline, which
 * outranks any stylesheet rule, so the breakpoint is resolved here rather than
 * with a media query in index.css.
 * @param isOverlay - Whether the platform draws overlay scrollbars.
 */
const setScrollbarOverlayGutter = (isOverlay: boolean) => {
  const mobile = window.matchMedia(MOBILE_QUERY);

  const apply = () => {
    document.documentElement.style.setProperty(
      '--scrollbar-overlay-gutter',
      isOverlay && !mobile.matches ? GUTTER_WIDTH : NO_GUTTER,
    );
  };

  apply();

  // Rotating a device or resizing a window crosses the breakpoint without
  // reloading, so the width is re-applied rather than read once at startup.
  mobile.addEventListener('change', apply);
};

/**
 * Whether an element can show a vertical scrollbar right now.
 *
 * Both halves matter. The computed overflow says a scrollbar is allowed, and
 * the measurement says the content actually overflows — a container that is
 * declared scrollable but currently fits shows no thumb and needs no gutter.
 *
 * Fixed elements are excluded outright: full-screen overlays (Floating UI
 * writes an unconditional `overflow: auto` onto the one behind every modal,
 * dropdown and tooltip) span the viewport rather than sitting next to its edge,
 * so padding them shifts the whole layer instead of clearing the scrollbar.
 * @param element - The element to measure.
 * @returns Whether a scrollbar is currently painted over the element's edge.
 */
const hasVisibleScrollbar = (element: HTMLElement) => {
  const { overflowY, position } = getComputedStyle(element);

  if (position === 'fixed') return false;
  if (overflowY !== 'auto' && overflowY !== 'scroll') return false;

  return element.scrollHeight > element.clientHeight;
};

/**
 * Tags the elements that currently paint a scrollbar with `data-scroll-gutter`,
 * which index.css hooks the padding onto. CSS can select neither "an element
 * that scrolls" nor "an element that overflows", so both are resolved here.
 *
 * Only elements whose overflow is declared scrollable are observed and
 * measured, so the reflow cost stays proportional to the number of scroll
 * containers rather than to the size of the document.
 * @param root - The subtree to scan for scroll containers.
 * @param observer - Resize observer watching the containers and their content.
 */
const tagScrollContainers = (root: ParentNode, observer: ResizeObserver) => {
  const elements = root.querySelectorAll<HTMLElement>('*');
  const containers: HTMLElement[] = [];

  elements.forEach((element) => {
    const { overflowY, position } = getComputedStyle(element);
    const scrollable =
      (overflowY === 'auto' || overflowY === 'scroll') && position !== 'fixed';

    if (!scrollable) {
      delete element.dataset.scrollGutter;
      return;
    }

    // Watching the container catches viewport resizes; watching its children
    // catches content growing or shrinking inside a container whose own box
    // never changes. Re-observing an element already being watched is a no-op.
    observer.observe(element);
    Array.from(element.children).forEach((child) => observer.observe(child));

    containers.push(element);
  });

  applyScrollGutters(containers);
};

/**
 * Applies or removes the gutter tag across a batch of elements.
 *
 * Every element is measured before any is written to. Interleaving the two
 * would thrash layout: each attribute write invalidates it, so the next
 * `scrollHeight` read would force a synchronous recalculation.
 * @param elements - The scroll containers to re-measure.
 */
const applyScrollGutters = (elements: Iterable<HTMLElement>) => {
  const measured = Array.from(elements, (element): [HTMLElement, boolean] => [
    element,
    hasVisibleScrollbar(element),
  ]);

  measured.forEach(([element, needsGutter]) => {
    if (needsGutter) {
      element.dataset.scrollGutter = '';
    } else {
      delete element.dataset.scrollGutter;
    }
  });
};

/**
 * Reserves room for overlay scrollbars across the whole app, but only where a
 * scrollbar is actually painted.
 *
 * Two observers are needed because overflow changes for two unrelated reasons.
 * Mutations mount and unmount scroll containers as the user navigates, while
 * resizes change whether existing content overflows — a window resize, a font
 * or image finishing loading, or a section expanding all move an element across
 * the threshold without touching the DOM. Neither observer is ever
 * disconnected: both live for as long as the document does.
 */
export const setScrollbarGutter = () => {
  const isOverlay = detectOverlayScrollbars();

  setScrollbarOverlayGutter(isOverlay);

  // Classic scrollbars already reserve their own gutter, so there is nothing
  // to tag and no reason to pay for the observers.
  if (!isOverlay) return;

  const resizeObserver = new ResizeObserver((entries) => {
    // Children are observed to detect content growth, so an entry may be a
    // child rather than the container itself. Re-measure both candidates and
    // let hasVisibleScrollbar reject whichever is not a scroll container.
    const candidates = new Set<HTMLElement>();

    entries.forEach((entry) => {
      const element = entry.target as HTMLElement;

      candidates.add(element);
      if (element.parentElement) candidates.add(element.parentElement);
    });

    applyScrollGutters(candidates);
  });

  tagScrollContainers(document, resizeObserver);

  // React commits arrive in bursts, and a full scan reads the computed style of
  // every element in the document. Collapsing a burst into a single scan before
  // the next paint keeps the cost per frame flat no matter how many mutations
  // the commit produced.
  let scheduled = 0;

  const mutationObserver = new MutationObserver(() => {
    if (scheduled) return;

    scheduled = requestAnimationFrame(() => {
      scheduled = 0;
      tagScrollContainers(document, resizeObserver);
    });
  });

  mutationObserver.observe(document.body, { childList: true, subtree: true });
};
